import { Container, Row, Col, Spinner } from "react-bootstrap";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDashboardData } from "./core/useDashboardData";
import {
  useQueryResponse,
  useQueryResponseLoading,
} from "../check-user-funds/user-funds-list/core/QueryResponseProvider";
import { KTSVG } from "../../../_mcp/helpers";
import { useAuth } from "../../modules/auth";

// Import komponen terpisah
import ModernCard from "./components/ModernCard";
import StatisticCard from "./components/StatisticCard";
import CircularProgress from "./components/CircularProgress";
import ModernProgressBar from "./components/ModernProgressBar";

const formatIDR = (value: unknown) => {
  const num = typeof value === "string" ? Number(value) : (value as number);
  if (num === undefined || num === null || isNaN(num)) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

const formatNumber = (value: number) => {
  return new Intl.NumberFormat("id-ID").format(value);
};

const formatPercentage = (value: number) => {
  return `${value.toFixed(1)}%`;
};

const HomePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { response } = useQueryResponse();
  const isLoading = useQueryResponseLoading();

  // Determine user role
  const getUserRole = () => {
    if (!currentUser?.auth?.role) return null;
    return currentUser.auth.role;
  };

  const userRole = getUserRole();
  // For merchant route: Only MERCHANT_ADMIN can access user management
  const canCreateUser = userRole === "MERCHANT_ADMIN";
  // Quick Actions card visible for MERCHANT_ADMIN, MERCHANT_MAKER, and MERCHANT_APPROVAL
  const canViewQuickActions = userRole === "MERCHANT_ADMIN" || userRole === "MERCHANT_MAKER" || userRole === "MERCHANT_APPROVAL";
  
  // Different actions for different roles
  const isMerchantAdmin = userRole === "MERCHANT_ADMIN";
  const isMerchantMaker = userRole === "MERCHANT_MAKER";
  const isMerchantApproval = userRole === "MERCHANT_APPROVAL";

  // Debug: Log role untuk troubleshooting
  console.log('Current user role:', userRole);
  console.log('Can create user:', canCreateUser);
  console.log('Can view quick actions:', canViewQuickActions);
  console.log('Is merchant admin:', isMerchantAdmin);
  console.log('Is merchant maker:', isMerchantMaker);
  console.log('Is merchant approval:', isMerchantApproval);
  console.log('Environment:', process.env.NODE_ENV);

  const data: any = response?.detailData.docs;
  const first = Array.isArray(data) ? data[0] : undefined;
  const merchantId = first?.merchantId;
  const merchantName: string | undefined = first?.merchantName;

  // Get dashboard data from database
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    stats,
    userFundSummary,
    transactionSummary,
    refresh
  } = useDashboardData({ merchantId }, 60000);

  // Calculate analytics
  const totalTransactions = (stats?.todayTransactions || 0) + (stats?.successfulTransactions || 0) + (stats?.pendingTransactions || 0) + (stats?.failedTransactions || 0);
  
  // Use real data when available, fallback to original data
  const amount = formatIDR(userFundSummary?.totalBalance || first?.amount || 0);
  const availableFund = formatIDR(userFundSummary?.availableFunds || first?.availableFund || 0);
  const holdAmount = formatIDR(userFundSummary?.holdAmount || first?.holdAmount || 0);
  const holdAmountProvider = formatIDR(userFundSummary?.holdAmountProvider || first?.holdAmountProvider || 0);

  return (
    <div style={{ 
      minHeight: '100vh',
      position: 'relative'
    }}>
      {/* Animated Background Pattern */}
      <div 
        className="position-absolute w-100 h-100"
        style={{
          background: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.3
        }}
      />
      
      <Container fluid className="py-4 position-relative">
        {/* Modern Header with Glass Effect */}
        <ModernCard 
          className="d-flex align-items-center justify-content-between mb-5"
          style={{
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <div className="d-flex align-items-center">
            <div 
              className="rounded-4 me-4 d-flex align-items-center justify-content-center position-relative bg-primary"
              style={{ 
                width: '60px', 
                height: '60px',
                color: 'white',
                fontSize: '24px',
                boxShadow: '0 20px 40px rgba(0, 123, 255, 0.3)'
              }}
            >
              <KTSVG path='/media/icons/duotune/graphs/gra001.svg' className='svg-icon-3' svgClassName='text-white' />
            </div>
            <div>
              <h2 className="mb-1 fw-bold text-grey" style={{ fontSize: '2.5rem', letterSpacing: '-1px' }}>
                Dashboard Analytics
              </h2>
              <p className="mb-0 text-grey">
                Real-time Business Intelligence
                {userFundSummary?.lastUpdated && (
                  <span className="ms-2 small">
                    • Last updated: {new Date(userFundSummary.lastUpdated).toLocaleString('id-ID')}
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            {merchantName && (
              <div 
                className="px-4 py-2 rounded-pill fw-semibold bg-primary"
                style={{ 
                  color: 'white',
                  fontSize: '14px',
                  boxShadow: '0 10px 25px rgba(0, 123, 255, 0.25)'
                }}
              >
                <KTSVG path='/media/icons/duotune/general/gen025.svg' className='svg-icon-2 me-2' svgClassName='text-white' />
                {merchantName}
              </div>
            )}
            {isDashboardLoading && (
              <Spinner animation="border" size="sm" style={{ color: 'white' }} className="me-2" />
            )}
            <button 
              className="btn px-4 py-2 rounded-pill fw-semibold border-0 position-relative overflow-hidden bg-primary"
              style={{ 
                color: 'white',
                transition: 'all 0.3s ease'
              }}
              onClick={refresh}
              disabled={isDashboardLoading}
              title="Refresh Data"
              onMouseEnter={(e) => (e.target as HTMLElement).style.opacity = '0.8'}
              onMouseLeave={(e) => (e.target as HTMLElement).style.opacity = '1'}
            >
              <KTSVG path='/media/icons/duotune/arrows/arr082.svg' className='svg-icon-2 me-2' svgClassName='text-white' />
              Refresh
            </button>
          </div>
        </ModernCard>

        {isLoading || isDashboardLoading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="text-center">
              <div 
                className="spinner-border mb-3"
                style={{ 
                  color: 'white',
                  width: '3rem',
                  height: '3rem',
                  borderWidth: '4px'
                }}
              />
              <div className="text-white-50">Loading analytics...</div>
            </div>
          </div>
        ) : isDashboardError ? (
          <div className="d-flex justify-content-center py-5">
            <div className="text-center">
              <div className="text-danger mb-3">
                <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-5x' svgClassName='text-danger' />
              </div>
              <div className="h5 text-white mb-2">Failed to Load Data</div>
              <div className="text-white-50 mb-3">Unable to fetch dashboard data from the database</div>
              <button 
                className="btn btn-primary px-4 py-2 rounded-pill fw-semibold"
                onClick={refresh}
              >
                <KTSVG path='/media/icons/duotune/arrows/arr082.svg' className='svg-icon-2 me-2' svgClassName='text-white' />
                Retry
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Ultra Modern Statistics Cards */}
            

            {/* Ultra Modern Fund Balance Section */}
            <Row className="g-4 mb-5">
              <Col lg={canViewQuickActions ? 4 : 6}>
                <ModernCard className="h-100">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">
                        <span className="bg-primary rounded p-2 me-2 d-inline-flex">
                          <KTSVG path='/media/icons/duotune/finance/fin006.svg' className='svg-icon-2' svgClassName='text-white' />
                        </span>
                        Available Funds
                      </h5>
                      <p className="mb-0 text-muted small">Real-time balance overview</p>
                    </div>
                  </div>
                  
                  <Row className="g-3">
                    <Col xs={12}>
                      <div 
                        className="p-4 rounded-3 text-center position-relative overflow-hidden"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(255,167,38,0.1) 0%, rgba(255,167,38,0.05) 100%)',
                          border: '1px solid rgba(255,167,38,0.2)'
                        }}
                      >
                        <div className="text-muted small text-uppercase mb-2 fw-semibold">Total Amount</div>
                        <div className="h4 fw-bold text-dark mb-0">{amount}</div>
                        <div className="position-absolute top-0 end-0 mt-2 me-2 opacity-25">
                          <KTSVG path='/media/icons/duotune/finance/fin001.svg' className='svg-icon-2' svgClassName='text-muted' />
                        </div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div 
                        className="p-4 rounded-3 text-center position-relative overflow-hidden"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(255,167,38,0.1) 0%, rgba(255,167,38,0.05) 100%)',
                          border: '1px solid rgba(255,167,38,0.2)'
                        }}
                      >
                        <div className="text-muted small text-uppercase mb-2 fw-semibold">Available Fund</div>
                        <div className="h4 fw-bold text-dark mb-0">{availableFund}</div>
                        <div className="position-absolute top-0 end-0 mt-2 me-2 opacity-25">
                          <KTSVG path='/media/icons/duotune/finance/fin010.svg' className='svg-icon-2' svgClassName='text-muted' />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </ModernCard>
              </Col>

              <Col lg={canViewQuickActions ? 4 : 6}>
                <ModernCard className="h-100">
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                      <h5 className="fw-bold mb-1 text-dark">
                        <span className="bg-primary rounded p-2 me-2 d-inline-flex">
                          <KTSVG path='/media/icons/duotune/general/gen044.svg' className='svg-icon-2' svgClassName='text-white' />
                        </span>
                        Hold Funds
                      </h5>
                      <p className="mb-0 text-muted small">Locked balance management</p>
                    </div>
                  </div>
                  
                  <Row className="g-3">
                    <Col xs={12}>
                      <div 
                        className="p-4 rounded-3 text-center position-relative overflow-hidden"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(255,167,38,0.1) 0%, rgba(255,167,38,0.05) 100%)',
                          border: '1px solid rgba(255,167,38,0.2)'
                        }}
                      >
                        <div className="text-muted small text-uppercase mb-2 fw-semibold">Hold Amount</div>
                        <div className="h4 fw-bold text-dark mb-0">{holdAmount}</div>
                        <div className="position-absolute top-0 end-0 mt-2 me-2 opacity-25">
                          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-2' svgClassName='text-muted' />
                        </div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div 
                        className="p-4 rounded-3 text-center position-relative overflow-hidden"
                        style={{ 
                          background: 'linear-gradient(135deg, rgba(255,167,38,0.1) 0%, rgba(255,167,38,0.05) 100%)',
                          border: '1px solid rgba(255,167,38,0.2)'
                        }}
                      >
                        <div className="text-muted small text-uppercase mb-2 fw-semibold">Provider Hold</div>
                        <div className="h4 fw-bold text-dark mb-0">{holdAmountProvider}</div>
                        <div className="position-absolute top-0 end-0 mt-2 me-2 opacity-25">
                          <KTSVG path='/media/icons/duotune/general/gen025.svg' className='svg-icon-2' svgClassName='text-muted' />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </ModernCard>
              </Col>
              
              {/* Quick Actions Card - Visible for MERCHANT_ADMIN, MERCHANT_MAKER, and MERCHANT_APPROVAL */}
              {canViewQuickActions && (
                <Col lg={4}>
                  <ModernCard className="h-100">
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div>
                        <h5 className="fw-bold mb-1 text-dark">
                          <span className="bg-primary rounded p-2 me-2 d-inline-flex">
                            <KTSVG path='/media/icons/duotune/general/gen019.svg' className='svg-icon-2' svgClassName='text-white' />
                          </span>
                          Quick Actions
                        </h5>
                        <p className="mb-0 text-muted small">Fast access to common tasks</p>
                      </div>
                    </div>
                    
                    {/* Quick Action Buttons - Different for each role */}
                    <div className="d-grid gap-3">
                      {/* MERCHANT_ADMIN: Only Create New User */}
                      {isMerchantAdmin && (
                        <button 
                          className="btn btn-lg d-flex align-items-center justify-content-between p-4 rounded-3 border-0 position-relative overflow-hidden bg-primary"
                          style={{ 
                            color: 'white',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 8px 25px rgba(0, 123, 255, 0.3)'
                          }}
                          onClick={() => navigate("/merchant/user-management/create")}
                          onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-2px)'}
                          onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
                        >
                          <div className="d-flex align-items-center">
                            <div 
                              className="rounded-3 me-3 d-flex align-items-center justify-content-center"
                              style={{ 
                                width: '48px', 
                                height: '48px',
                                background: 'rgba(255,255,255,0.2)'
                              }}
                            >
                              <KTSVG path='/media/icons/duotune/general/gen005.svg' className='svg-icon-2' svgClassName='text-white' />
                            </div>
                            <div className="text-start">
                              <div className="fw-bold">Create New User</div>
                              <div className="small opacity-75">Add new user account</div>
                            </div>
                          </div>
                          <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' svgClassName='text-white' />
                        </button>
                      )}

                      {/* MERCHANT_MAKER: Top Up Funds, Create New Transfer, and Create New User Fund */}
                      {isMerchantMaker && (
                        <>
                          {/* Top Up Button */}
                          <button 
                            className="btn btn-lg d-flex align-items-center justify-content-between p-4 rounded-3 border-0 position-relative overflow-hidden bg-primary"
                            style={{ 
                              color: 'white',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 8px 25px rgba(0, 123, 255, 0.3)'
                            }}
                            onClick={() => navigate('/merchant/user-funds')}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-3 me-3 d-flex align-items-center justify-content-center"
                                style={{ 
                                  width: '48px', 
                                  height: '48px',
                                  background: 'rgba(255,255,255,0.2)'
                                }}
                              >
                                <KTSVG path='/media/icons/duotune/finance/fin006.svg' className='svg-icon-2' svgClassName='text-white' />
                              </div>
                              <div className="text-start">
                                <div className="fw-bold">Top Up Funds</div>
                                <div className="small opacity-75">Add balance to wallet</div>
                              </div>
                            </div>
                            <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' svgClassName='text-white' />
                          </button>

                          {/* Create New Transfer Button */}
                          <button 
                            className="btn btn-lg d-flex align-items-center justify-content-between p-4 rounded-3 border-0 position-relative overflow-hidden bg-primary"
                            style={{ 
                              color: 'white',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 8px 25px rgba(0, 123, 255, 0.3)'
                            }}
                            onClick={() => navigate("/merchant/transfers", { state: { openModal: true } })}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-3 me-3 d-flex align-items-center justify-content-center"
                                style={{ 
                                  width: '48px', 
                                  height: '48px',
                                  background: 'rgba(255,255,255,0.2)'
                                }}
                              >
                                <KTSVG path='/media/icons/duotune/finance/fin009.svg' className='svg-icon-2' svgClassName='text-white' />
                              </div>
                              <div className="text-start">
                                <div className="fw-bold">Create New Transfer</div>
                                <div className="small opacity-75">Send money transfer</div>
                              </div>
                            </div>
                            <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' svgClassName='text-white' />
                          </button>

                          {/* Create New User Fund Button */}
                          <button 
                            className="btn btn-lg d-flex align-items-center justify-content-between p-4 rounded-3 border-0 position-relative overflow-hidden bg-primary"
                            style={{ 
                              color: 'white',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 8px 25px rgba(0, 123, 255, 0.3)'
                            }}
                            onClick={() => navigate("/merchant/user-funds", { state: { addNew: true } })}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-3 me-3 d-flex align-items-center justify-content-center"
                                style={{ 
                                  width: '48px', 
                                  height: '48px',
                                  background: 'rgba(255,255,255,0.2)'
                                }}
                              >
                                <KTSVG path='/media/icons/duotune/finance/fin001.svg' className='svg-icon-2' svgClassName='text-white' />
                              </div>
                              <div className="text-start">
                                <div className="fw-bold">Create New User Fund</div>
                                <div className="small opacity-75">Manage user fund records</div>
                              </div>
                            </div>
                            <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' svgClassName='text-white' />
                          </button>
                        </>
                      )}

                      {/* MERCHANT_APPROVAL: Check Status Transfer and Check Status Topup */}
                      {isMerchantApproval && (
                        <>
                          {/* Check Status Transfer Button */}
                          <button 
                            className="btn btn-lg d-flex align-items-center justify-content-between p-4 rounded-3 border-0 position-relative overflow-hidden bg-primary"
                            style={{ 
                              color: 'white',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 8px 25px rgba(0, 123, 255, 0.3)'
                            }}
                            onClick={() => navigate("/merchant/transfers")}
                            onMouseEnter={(e) => (e.target as HTMLElement).style.transform = 'translateY(-2px)'}
                            onMouseLeave={(e) => (e.target as HTMLElement).style.transform = 'translateY(0)'}
                          >
                            <div className="d-flex align-items-center">
                              <div 
                                className="rounded-3 me-3 d-flex align-items-center justify-content-center"
                                style={{ 
                                  width: '48px', 
                                  height: '48px',
                                  background: 'rgba(255,255,255,0.2)'
                                }}
                              >
                                <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-2' svgClassName='text-white' />
                              </div>
                              <div className="text-start">
                                <div className="fw-bold">Check Status Transfer</div>
                                <div className="small opacity-75">Review transfer status</div>
                              </div>
                            </div>
                            <KTSVG path='/media/icons/duotune/arrows/arr071.svg' className='svg-icon-2' svgClassName='text-white' />
                          </button>
                        </>
                      )}
                    </div>
                  </ModernCard>
                </Col>
              )}
            </Row>
            
          </>
        )}
      </Container>
    </div>
  );
};

export default HomePage;
